import { Calendar, SelectLabel } from '@weezevent/nacre';
import moment from 'moment-timezone';
import React from 'react';
import { useTranslation } from 'react-i18next';
import LoadingCard from '../../../../../components/loadingCard';
import SessionModel from '../../../../../models/sessionModel';
import { isSameDay } from '../../../../sessions/list/components/listPreview';
import useAppContext from '../../../../../context/useAppContext';
import useDateFormat from '../../../../../components/utils/useDateFormat';
import css from '../../styles.module.css';

export const toObject = o => (o.object ? o.object : o);
export const sessionIsAvailable = s => s.quota.total === null || s.quota.total - s.quota.taken > 0;

export default function CalendarSessionsInput({
    selectLabel,
    selectPlaceholder,
    daySelected,
    onDaySelected,
    sessionSelected,
    onSessionSelected,
    sessionsDays,
    loadingSessions,
    loading
}) {
    const { i18n } = useTranslation();
    const [{ organization, event }] = useAppContext();

    const [loadingDays, setLoadingDays] = React.useState(false);
    const [daysWithSessions, setDaysWithSessions] = React.useState([]);

    const locale = i18n.language;

    //important for set locale moment used by Calendar
    React.useEffect(() => {
        moment.locale(i18n.language);
    }, [i18n.language]);

    React.useEffect(() => {
        if (!organization || !event) {
            return;
        }
        setLoadingDays(true);
        new SessionModel({})
            .countSessionsByDays({ organizationId: organization.id, eventId: event.id, query: { enabled: true, include_past_sessions: true } })
            .then(response => {
                setDaysWithSessions(response.response);
            })
            .finally(() => setLoadingDays(false));
    }, [organization, event]);

    const sessionFormat = React.useCallback(
        date => {
            const [, properTime] = useDateFormat({
                date,
                event,
                timeStyle: 'short'
            });
            return `${properTime}`;
        },
        [useDateFormat]
    );

    const isDayInPast = React.useCallback(
        day => {
            const today = moment.tz(event.timezone).format('YYYY-MM-DD');
            const dayFormatted = moment.tz(day, event.timezone).format('YYYY-MM-DD');
            return dayFormatted < today;
        },
        [event.timezone]
    );

    const objectSessionsDays = React.useMemo(() => sessionsDays?.map(toObject), [sessionsDays]);

    const sessionsOptions = React.useMemo(
        () =>
            objectSessionsDays?.map(s => ({
                disabled: !sessionIsAvailable(s),
                text: sessionFormat(s.start_date),
                value: s.uuid,
                object: s
            })),
        [objectSessionsDays, sessionFormat]
    );

    if (loading || loadingDays) {
        return <LoadingCard loading />;
    }

    return (
        <span className={css['select-sessions']}>
            <Calendar
                locale={locale}
                timezone={event.timezone}
                dateSelected={daySelected}
                onChange={date => onDaySelected(date)}
                isDayHighlighted={day1 => {
                    if (isDayInPast(day1)) return false;
                    return daysWithSessions && Object.entries(daysWithSessions).some(([day2, quotaDay]) => isSameDay(day1, day2) && quotaDay > 0);
                }}
                isDayBlocked={day1 => {
                    if (isDayInPast(day1)) return true;
                    return loadingSessions || (daysWithSessions && !Object.entries(daysWithSessions).some(([day2, quotaDay]) => isSameDay(day1, day2) && quotaDay > 0));
                }}
            />
            <SelectLabel
                loading={daySelected && loadingSessions}
                label={selectLabel}
                placeholder={selectPlaceholder}
                disabled={!daySelected || loadingSessions}
                options={!loadingSessions ? sessionsOptions : []}
                value={sessionSelected?.uuid || null}
                onChange={(value, option) => {
                    onSessionSelected(option.object);
                }}
            />
        </span>
    );
}
